import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

import API_BASE from '../../utils/api';
const API = API_BASE;

const money = (value) =>
  `Rs. ${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const statusClass = (status) => {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'overdue':
      return 'bg-red-100 text-red-800';
    case 'partial':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const PrincipalFees = () => {
  const [fees, setFees] = useState([]);
  const [stats, setStats] = useState({ totalCollected: 0, pendingAmount: 0, byStatus: [] });
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ status: '', studentId: '' });
  const [bulkFormData, setBulkFormData] = useState({
    classId: '',
    feeType: 'tuition',
    amount: '',
    dueDate: '',
    description: '',
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.studentId) params.append('studentId', filters.studentId);

      const [feesRes, statsRes, classesRes, studentsRes] = await Promise.all([
        axios.get(`${API}/fees?${params.toString()}`),
        axios.get(`${API}/fees/stats/summary`),
        axios.get(`${API}/classes`),
        axios.get(`${API}/students`),
      ]);

      setFees(Array.isArray(feesRes.data) ? feesRes.data : []);
      setStats(statsRes.data || { totalCollected: 0, pendingAmount: 0, byStatus: [] });
      setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
      setStudents(Array.isArray(studentsRes.data) ? studentsRes.data : studentsRes.data?.students || []);
    } catch (err) {
      console.error('Error fetching fees data:', err);
      setError('Failed to load fee data.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const response = await axios.post(`${API}/fees/bulk/create`, bulkFormData);
      setSuccess(response.data.message || 'Fee records created successfully.');
      setShowBulkModal(false);
      setBulkFormData({
        classId: '',
        feeType: 'tuition',
        amount: '',
        dueDate: '',
        description: '',
      });
      fetchData();
      window.setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to create fees.');
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  const paidCount = stats.byStatus?.find((item) => item._id === 'paid')?.count || 0;
  const pendingCount = stats.byStatus?.find((item) => item._id === 'pending')?.count || 0;
  const overdueCount = stats.byStatus?.find((item) => item._id === 'overdue')?.count || 0;

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="sticky top-0 z-10 bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800">Fees Management</h1>
              <p className="text-sm text-gray-500">Review fee records and create new class fee schedules.</p>
            </div>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          {success && <div className="mb-4 rounded border border-green-400 bg-green-100 px-4 py-3 text-green-700">{success}</div>}
          {error && <div className="mb-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">{error}</div>}

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg bg-white p-6 shadow"><p className="text-sm text-gray-500">Total Collected</p><p className="text-2xl font-bold text-green-600">{money(stats.totalCollected)}</p></div>
            <div className="rounded-lg bg-white p-6 shadow"><p className="text-sm text-gray-500">Pending Amount</p><p className="text-2xl font-bold text-yellow-600">{money(stats.pendingAmount)}</p></div>
            <div className="rounded-lg bg-white p-6 shadow"><p className="text-sm text-gray-500">Paid / Pending / Overdue</p><p className="text-2xl font-bold">{paidCount} / {pendingCount} / {overdueCount}</p></div>
            <div className="rounded-lg bg-white p-6 shadow"><p className="text-sm text-gray-500">Total Records</p><p className="text-2xl font-bold text-indigo-600">{fees.length}</p></div>
          </div>

          <div className="mb-6 flex gap-3">
            <button
              type="button"
              onClick={() => setShowBulkModal(true)}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
            >
              Create Fees for Class
            </button>
          </div>

          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-blue-800">
            Fee reminder SMS sending is handled by the admin panel only.
          </div>

          <div className="mb-6 flex gap-4 rounded-lg bg-white p-4 shadow">
            <select
              value={filters.status}
              onChange={(event) => setFilters({ ...filters, status: event.target.value })}
              className="rounded-md border px-3 py-2"
            >
              <option value="">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="partial">Partial</option>
            </select>
            <select
              value={filters.studentId}
              onChange={(event) => setFilters({ ...filters, studentId: event.target.value })}
              className="flex-1 rounded-md border px-3 py-2"
            >
              <option value="">All Students</option>
              {students.map((student) => (
                <option key={student._id} value={student._id}>
                  {student.name} ({student.rollNumber})
                </option>
              ))}
            </select>
          </div>

          <div className="overflow-hidden rounded-lg bg-white shadow">
            <div className="border-b px-6 py-4">
              <h2 className="text-lg font-semibold">Fee Records</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Student</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {fees.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-8 text-center text-gray-500">No fee records found.</td>
                    </tr>
                  ) : (
                    fees.map((fee) => (
                      <tr key={fee._id}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {fee.studentId?.name || 'Unknown'} <span className="text-xs text-gray-400">({fee.studentId?.rollNumber})</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap capitalize">{fee.feeType}</td>
                        <td className="px-4 py-3 whitespace-nowrap font-medium">{money(fee.amount)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{new Date(fee.dueDate).toLocaleDateString()}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(fee.status)}`}>
                            {fee.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {showBulkModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <h2 className="mb-4 text-xl font-semibold">Create Fees for Class</h2>
              <form onSubmit={handleBulkCreate} className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Class</label>
                  <select
                    required
                    value={bulkFormData.classId}
                    onChange={(event) => setBulkFormData({ ...bulkFormData, classId: event.target.value })}
                    className="w-full rounded-md border px-3 py-2"
                  >
                    <option value="">Select Class</option>
                    {classes.map((classItem) => (
                      <option key={classItem._id} value={classItem._id}>
                        {classItem.className} - {classItem.section}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Fee Type</label>
                  <select
                    value={bulkFormData.feeType}
                    onChange={(event) => setBulkFormData({ ...bulkFormData, feeType: event.target.value })}
                    className="w-full rounded-md border px-3 py-2"
                  >
                    <option value="tuition">Tuition</option>
                    <option value="transport">Transport</option>
                    <option value="library">Library</option>
                    <option value="sports">Sports</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Amount</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={bulkFormData.amount}
                    onChange={(event) => setBulkFormData({ ...bulkFormData, amount: event.target.value })}
                    className="w-full rounded-md border px-3 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Due Date</label>
                  <input
                    type="date"
                    required
                    value={bulkFormData.dueDate}
                    onChange={(event) => setBulkFormData({ ...bulkFormData, dueDate: event.target.value })}
                    className="w-full rounded-md border px-3 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    value={bulkFormData.description}
                    onChange={(event) => setBulkFormData({ ...bulkFormData, description: event.target.value })}
                    className="w-full rounded-md border px-3 py-2"
                    placeholder="Optional"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setShowBulkModal(false)} className="rounded-md border px-4 py-2 text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrincipalFees;
